import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import { getPeriodRange, toISTDateString, type PeriodKey } from '@/lib/periods';

interface Args {
  outletId: string | 'all';
  period: PeriodKey;
}

export interface HeatmapResult {
  /** matrix[dow][hour] = avg orders per occurrence of (dow,hour) in the period */
  matrix: number[][];
  max: number;
  totalOrders: number;
  /** number of distinct dates included */
  days: number;
}

export function useHourlyHeatmap({ outletId, period }: Args) {
  const range = getPeriodRange(period === 'custom' ? '28d' : period);

  return useQuery<HeatmapResult>({
    queryKey: ['hourly-heatmap', outletId, range.start.toISOString(), range.end.toISOString()],
    queryFn: async () => {
      const start = toISTDateString(range.start);
      const end = toISTDateString(new Date(range.end.getTime() - 1));

      let q = marketing
        .from('mv_hourly_orders')
        .select('outlet_id, date, dow, hour, orders')
        .gte('date', start)
        .lte('date', end);
      if (outletId !== 'all') q = q.eq('outlet_id', outletId);

      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as unknown as Array<{
        date: string;
        dow: number;
        hour: number;
        orders: number;
      }>;

      // sum per (dow, hour) and count occurrences (distinct dates per dow)
      const sums: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
      const countsByDow = new Map<number, Set<string>>();
      let totalOrders = 0;
      for (const r of rows) {
        const row = sums[r.dow];
        if (row) row[r.hour] = (row[r.hour] ?? 0) + Number(r.orders ?? 0);
        totalOrders += Number(r.orders ?? 0);
        if (!countsByDow.has(r.dow)) countsByDow.set(r.dow, new Set());
        countsByDow.get(r.dow)!.add(r.date);
      }

      const matrix: number[][] = sums.map((row, dow) => {
        const occurrences = countsByDow.get(dow)?.size ?? 0;
        if (occurrences === 0) return row.map(() => 0);
        return row.map((v) => v / occurrences);
      });
      const max = matrix.flat().reduce((m, v) => Math.max(m, v), 0);
      const days = new Set(rows.map((r) => r.date)).size;

      return { matrix, max, totalOrders, days };
    },
    staleTime: 60_000,
  });
}
